import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { PRODUCTS_SORT_OPTIONS } from "@modules/shop/components/Products/sort-options";
import { Checkbox } from "@ui/components/ui/checkbox";
import { Label } from "@ui/components/ui/label";
import { Input } from "@ui/components/ui/input";
import { Button } from "@ui/components/ui/button";
import { ActiveFilterPills } from "@modules/shop/components/Products/ActiveFilterPills";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/components/ui/select";
import { useState } from "react";
import { useDebouncedFunction } from "@modules/shared/hooks/useDebouncedFunction";
import type { GetCollectionByHandleQuery } from "@gql/types";
import type { useProductFilters } from "@modules/shop/hooks/useProductFilters";

type ApiFilter = NonNullable<
  GetCollectionByHandleQuery["collection"]
>["products"]["filters"][number];

type FilterSidebarProps = {
  productFilters: ReturnType<typeof useProductFilters>;
  sortKey: string;
  onSortChange: (value: string) => void;
  availableFilters?: Array<ApiFilter>;
  /** Unique product types present in the store, used to show only relevant type groups */
  availableProductTypes?: Array<string>;
};

/**
 * Display groups for product type filter.
 * `label` is shown in the UI; `values` are the actual Shopify productType values
 * sent to the API. One checkbox controls the whole group.
 */
export const PRODUCT_TYPE_DISPLAY_GROUPS = [
  {
    labelKey: "shoes",
    values: ["Handmade shoes", "Personalized handmade shoes", "Kožne papuče"],
  },
  { labelKey: "sandals", values: ["Sandale"] },
  { labelKey: "bags", values: ["Bag", "Torbe"] },
  { labelKey: "backpacks", values: ["Backpack"] },
  { labelKey: "leatherGoods", values: ["kožna galanterija"] },
  { labelKey: "belts", values: ["Remeni"] },
  { labelKey: "eyewearCases", values: ["Eyewear Cases"] },
  { labelKey: "keychains", values: ["Keychains"] },
  { labelKey: "pets", values: ["Ljubimci"] },
] as const;

export function FilterSidebar({
  productFilters,
  sortKey,
  onSortChange,
  availableFilters,
  availableProductTypes,
}: Readonly<FilterSidebarProps>) {
  const { t } = useTranslation();
  const { isFiltered, resetFilters } = productFilters;
  const [showSidebar, setShowSidebar] = useState(true);

  return (
    <aside className="hidden w-64 shrink-0 lg:block">
      <div className="sticky top-6 flex max-h-[calc(100dvh-8rem)] flex-col rounded-3xl bg-white p-5">
        {/* Active filter pills — shown above "Sakrij filtere" */}
        {isFiltered && (
          <div className="mb-3 shrink-0">
            <ActiveFilterPills productFilters={productFilters} />
          </div>
        )}

        {/* Header */}
        <div className="-mx-2 mb-3 flex shrink-0 items-center justify-between">
          <Button
            dark="ghost"
            size="extraSmall"
            onClick={() => setShowSidebar((v) => !v)}
            aria-expanded={showSidebar}
            aria-controls="filter-panel"
          >
            {showSidebar ? t("Filters.hideFilters") : t("Filters.showFilters")}
          </Button>
          {isFiltered && (
            <Button primary="ghost" size="extraSmall" onClick={resetFilters}>
              {t("Filters.resetFilters")}
            </Button>
          )}
        </div>

        {showSidebar && (
          <div
            id="filter-panel"
            className="min-h-0 flex-1 overflow-y-auto pr-1"
          >
            <FilterContent
              productFilters={productFilters}
              sortKey={sortKey}
              onSortChange={onSortChange}
              availableFilters={availableFilters}
              availableProductTypes={availableProductTypes}
            />
          </div>
        )}
      </div>
    </aside>
  );
}

// ── Shared filter content (used by both desktop sidebar and mobile drawer) ──

type FilterContentProps = {
  productFilters: ReturnType<typeof useProductFilters>;
  sortKey: string;
  onSortChange: (value: string) => void;
  /** Available filter options returned by the Shopify collection.products query */
  availableFilters?: Array<ApiFilter>;
  /** Unique prefix for element IDs to avoid collisions when rendered twice */
  idPrefix?: string;
  /** Unique product types present in the store; when provided, hides groups with no matching types */
  availableProductTypes?: Array<string>;
};

export function FilterContent({
  productFilters,
  sortKey,
  onSortChange,
  availableFilters = [],
  idPrefix = "",
  availableProductTypes,
}: Readonly<FilterContentProps>) {
  const { t } = useTranslation();
  const {
    filters,
    visibleFilters,
    setType,
    toggleColor,
    toggleSize,
    setPriceRange,
  } = productFilters;

  // Extract color and size options from API filter response
  const colorFilter = availableFilters.find((f) =>
    f.id.includes("color-pattern"),
  );
  const sizeFilter = availableFilters.find((f) => f.id.includes("option.size"));

  const colorOptions = colorFilter?.values ?? [];
  const sizeOptions = sizeFilter?.values.map((v) => v.label) ?? [];

  // Local price state for responsive input — debounced flush to URL
  const [localMin, setLocalMin] = useState(filters.minPrice || 0);
  const [localMax, setLocalMax] = useState(filters.maxPrice || 0);
  const [debouncedSetPriceRange] = useDebouncedFunction(setPriceRange, 600);

  // Sync local state when external filters reset
  const [prevMinPrice, setPrevMinPrice] = useState(filters.minPrice);
  if (prevMinPrice !== filters.minPrice) {
    setPrevMinPrice(filters.minPrice);
    setLocalMin(filters.minPrice || 0);
  }
  const [prevMaxPrice, setPrevMaxPrice] = useState(filters.maxPrice);
  if (prevMaxPrice !== filters.maxPrice) {
    setPrevMaxPrice(filters.maxPrice);
    setLocalMax(filters.maxPrice || 0);
  }

  return (
    <div className="space-y-6 pb-4">
      {/* ── Sort ─────────────────────────────────── */}
      <FilterSection title={t("Filters.sort")} defaultOpen={true}>
        <Select value={sortKey} onValueChange={onSortChange}>
          <SelectTrigger
            variant="box"
            inputSize="small"
            className="w-full"
            aria-label={t("Filters.sort")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent position="item-aligned">
            {PRODUCTS_SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.key} value={opt.key}>
                {t(`Filters.sortOptions.${opt.key}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterSection>

      {/* ── Product Type ─────────────────────────── */}
      <FilterSection title={t("Filters.productType.title")}>
        {PRODUCT_TYPE_DISPLAY_GROUPS.filter(({ values }) => {
          // If no availableProductTypes provided, show all groups (fallback)
          if (!availableProductTypes || availableProductTypes.length === 0)
            return true;
          // Only show groups that have at least one type present in the store
          return (values as ReadonlyArray<string>).some((v) =>
            availableProductTypes.includes(v),
          );
        }).map(({ labelKey, values }) => {
          const isChecked = (values as ReadonlyArray<string>).some((v) =>
            filters.type.includes(v),
          );
          const handleChange = () => {
            const current = filters.type;
            const allSelected = (values as ReadonlyArray<string>).every((v) =>
              current.includes(v),
            );
            if (allSelected) {
              setType(
                current.filter(
                  (v) => !(values as ReadonlyArray<string>).includes(v),
                ),
              );
            } else {
              const toAdd = (values as ReadonlyArray<string>).filter(
                (v) => !current.includes(v),
              );
              setType([...current, ...toAdd]);
            }
          };
          return (
            <FilterCheckbox
              key={labelKey}
              label={t(`Filters.productType.groups.${labelKey}` as never)}
              checked={isChecked}
              onChange={handleChange}
              idPrefix={idPrefix}
            />
          );
        })}
      </FilterSection>

      {/* ── Price Range ──────────────────────────── */}
      <FilterSection title={t("Filters.price.title")}>
        <div className="flex items-center gap-2">
          <Input
            variant="box"
            inputSize="small"
            type="number"
            min={0}
            step={1}
            placeholder={t("Filters.price.min")}
            value={localMin || ""}
            onChange={(e) => {
              const val = Number(e.target.value) || 0;
              setLocalMin(val);
              debouncedSetPriceRange(val, localMax);
            }}
            aria-label={t("Filters.price.min")}
          />
          <span className="text-text-3 text-sm">–</span>
          <Input
            variant="box"
            inputSize="small"
            type="number"
            min={0}
            step={1}
            placeholder={t("Filters.price.max")}
            value={localMax || ""}
            onChange={(e) => {
              const val = Number(e.target.value) || 0;
              setLocalMax(val);
              debouncedSetPriceRange(localMin, val);
            }}
            aria-label={t("Filters.price.max")}
          />
        </div>
      </FilterSection>

      {/* ── Color ────────────────────────────────── */}
      {visibleFilters.showColor && colorOptions.length > 0 && (
        <FilterSection title={t("Filters.color.title")}>
          <div className="flex flex-wrap gap-2">
            {colorOptions.map((colorValue) => {
              // Extract TaxonomyValue numeric ID from the filter value ID
              // ID format: "filter.v.t.shopify.color-pattern.gid-shopify-taxonomyvalue-{N}"
              const taxonomyId = colorValue.id.split("taxonomyvalue-")[1] ?? "";
              const colorName =
                TAXONOMY_ID_TO_COLOR_NAME[taxonomyId] ??
                colorValue.label.toLowerCase();
              const cssColor = COLOR_MAP[colorName] ?? "#ccc";
              const isSelected = filters.color.includes(colorName);
              return (
                <button
                  key={colorValue.id}
                  type="button"
                  onClick={() => toggleColor(colorName)}
                  title={colorValue.label}
                  aria-label={colorValue.label}
                  aria-pressed={isSelected}
                  className={`border-border-5 size-7 rounded-full border-2 transition-shadow ${
                    isSelected ? "ring-primary ring-2 ring-offset-2" : ""
                  }`}
                  style={{ background: cssColor }}
                />
              );
            })}
          </div>
        </FilterSection>
      )}

      {/* ── Size ─────────────────────────────────── */}
      {visibleFilters.showSize && sizeOptions.length > 0 && (
        <FilterSection title={t("Filters.size.title")}>
          <div className="flex flex-wrap justify-start gap-1.5">
            {sizeOptions.map((size) => {
              const isSelected = filters.size.includes(size);
              return (
                <Button
                  key={size}
                  type="button"
                  size="extraSmall"
                  dark={isSelected ? "default" : "outline"}
                  aria-label={size}
                  aria-pressed={isSelected}
                  onClick={() => toggleSize(size)}
                >
                  {size}
                </Button>
              );
            })}
          </div>
        </FilterSection>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function FilterSection({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-text-1 mb-2 flex w-full items-center justify-between text-sm font-semibold"
      >
        {title}
        <ChevronDown
          className={`text-text-4 size-4 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && children}
    </div>
  );
}

function FilterCheckbox({
  label,
  checked,
  onChange,
  idPrefix = "",
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  idPrefix?: string;
}) {
  const id = `${idPrefix}filter-${label}`;
  return (
    <div className="flex items-center gap-2 py-0.5">
      <Checkbox id={id} checked={checked} onCheckedChange={onChange} />
      <Label htmlFor={id} className="text-text-2 cursor-pointer text-sm">
        {label}
      </Label>
    </div>
  );
}

/**
 * Maps Shopify TaxonomyValue numeric IDs to English lowercase color names.
 * TaxonomyValue IDs are stable across locales — the API returns localized
 * labels, so we use the ID (extracted from the filter value's `id` field)
 * to look up the canonical English name used for URL state and i18n keys.
 */
const TAXONOMY_ID_TO_COLOR_NAME: Record<string, string> = {
  "6": "beige",
  "1": "black",
  "2": "blue",
  "7": "brown",
  "17": "clear",
  "8": "gray",
  "9": "green",
  "2865": "multicolor",
  "15": "navy",
  "11": "pink",
  "13": "red",
  "5": "silver",
  "3": "white",
  "14": "yellow",
};

const COLOR_MAP: Record<string, string> = {
  black: "#000000",
  white: "#FFFFFF",
  red: "#DC2626",
  blue: "#2563EB",
  green: "#16A34A",
  yellow: "#EAB308",
  pink: "#EC4899",
  brown: "#92400E",
  gray: "#6B7280",
  navy: "#1E3A5F",
  beige: "#D2B48C",
  silver: "#C0C0C0",
  multicolor: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
  clear: "rgba(200,200,200,0.3)",
};
